import moment from "moment";
import { FunctionComponent, useEffect, useState } from "react";
import { fetchGet, toQueryString } from "../../../../service/api_client";

interface NPDLogModel {
  id?: string;
  npd_id: string;
  operation?: string;
  user?: string;
  user_id?: any;
  log_type?: string;
  params?: any;
  created_at?: number;
}

interface NPDHistoryLogsProps {
  npd_id: string;
}

const NPD_LOG_NOISE_KEYS = ["bom_item_count"];

const GetNPDLogsApi = async (data: {
  npd_id: string;
  page?: number;
  count?: number;
}) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd/${
      data.npd_id
    }/logs${toQueryString({ page: data.page, count: data.count })}`;
    const res = await fetchGet(url);
    return res;
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

const renderParamValue = (value: any) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const NPDHistoryLogs: FunctionComponent<NPDHistoryLogsProps> = ({ npd_id }) => {
  const [logs, setLogs] = useState<NPDLogModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [count] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLoading(true);
    GetNPDLogsApi({ npd_id, page, count })
      .then((res) => {
        if (res.success && res.data) {
          setLogs(res.data.logs || []);
          if (res.data.metadata && res.data.metadata[0]) {
            setTotal(res.data.metadata[0].total);
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, npd_id]);

  const totalPages = Math.max(1, Math.ceil(total / count));

  return (
    <div className="p-2 w-full md:min-w-[360px]">
      <h2 className="font-bold text-lg text-gray-600 mb-2">
        NPD History — {npd_id}
      </h2>
      <div className="overflow-auto bg-gray-100 p-1 max-h-[70vh]">
        {!loading && logs.length === 0 && (
          <p className="text-center my-10 text-gray-500">No History!</p>
        )}
        {logs.map((log, i) => (
          <div key={log.id || i} className="mb-2 shadow rounded bg-white p-2">
            <p className="text-sm">
              <b>{log.user || "-"}</b>
              {log.operation ? ` ${log.operation}` : ` ${log.log_type}`}
            </p>
            {log.params && typeof log.params === "object" && (
              <div className="mt-1 text-xs text-gray-600">
                {Object.keys(log.params)
                  .filter((k) => !NPD_LOG_NOISE_KEYS.includes(k))
                  .map((k) => (
                    <div key={k}>
                      <span className="capitalize">{k.replace(/_/g, " ")}</span>
                      : {renderParamValue(log.params[k])}
                    </div>
                  ))}
              </div>
            )}
            <div className="flex justify-end items-end py-1">
              <span className="text-xs italic">
                {moment(log.created_at).format("lll")}
              </span>
            </div>
          </div>
        ))}
      </div>
      {total > count && (
        <div className="flex justify-between items-center mt-2 text-sm">
          <button
            disabled={page <= 1}
            className="px-2 py-1 rounded border disabled:opacity-40"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            className="px-2 py-1 rounded border disabled:opacity-40"
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default NPDHistoryLogs;
